import type { ParsedProfileResponse } from '@/api/gql/graphql';
import Card from '@/components/Card';
import CertificationsFormDialog from '@/components/profil-dialog/CertificationsFormDialog';
import ExperiencesFormDialog from '@/components/profil-dialog/ExperiencesFormDialog';
import FormationsFormDialog from '@/components/profil-dialog/FormationsFormDialog';
import LanguagesFormDialog from '@/components/profil-dialog/LanguagesFormDialog';
import { PersonalInfosFormDialog } from '@/components/profil-dialog/PersonalInfosFormDialog';
import { useProfileOnboarding } from '@/context/OnboarrdingContext';
import type {
  CertificationFormType,
  ExperienceFormType,
  FormationFormType,
  LanguagesFormType,
  ProfilFormType,
} from '@/types/form';
import { formValuesToProfile, profileToFormValues } from '@/utils/profile-form-converter';
import { ArrowLeftOutlined, ArrowRightOutlined, EditOutlined } from '@ant-design/icons';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Button, Modal } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const Route = createFileRoute('/import-profile/edit-profile')({
  component: RouteComponent,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      method: search.method === 'fill-from-zero' ? ('fill-from-zero' as const) : undefined,
    };
  },
});

type RecommendationCategory = 'summary' | 'experiences' | 'education';

function RouteComponent() {
  const { t } = useTranslation();
  const {
    profileResponse,
    resolvedRecommendations,
    evaluateCV,
    isLoading,
    currentMessage,
    updateProfile,
  } = useProfileOnboarding();
  const [experiencesDialogOpen, setExperiencesDialogOpen] = useState(false);
  const [formationDialogOpen, setFormationDialogOpen] = useState(false);
  const [languagesDialogOpen, setLanguagesDialogOpen] = useState(false);
  const [personalFormOpen, setPersonalFormOpen] = useState(false);
  const [certificationsDialogOpen, setCertificationsDialogOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const navigate = useNavigate();

  const { method } = Route.useSearch();

  const evaluateOnce = useRef(false);
  // modal to evaluate CV Open
  const [isEvaluateCVModalOpen, setIsEvaluateCVModalOpen] = useState(false);

  const {
    experiencesValues: initialExperiencesValues,
    formationsValues: initialFormationsValues,
    languagesValues: initialLanguagesValues,
    certificationsValues: initialCertificationsValues,
    personalValues: initialPersonalValues,
  } = profileToFormValues(profileResponse);

  const [experiencesValues, setExperiencesValues] =
    useState<ExperienceFormType>(initialExperiencesValues);
  const [formationsValues, setFormationsValues] =
    useState<FormationFormType>(initialFormationsValues);
  const [languagesValues, setLanguagesValues] = useState<LanguagesFormType>(initialLanguagesValues);
  const [certificationsValues, setCertificationsValues] = useState<CertificationFormType>(
    initialCertificationsValues
  );
  const [personalValues, setPersonalValues] = useState<ProfilFormType>(initialPersonalValues);

  useEffect(() => {
    if (profileResponse?.profile) {
      const {
        experiencesValues,
        formationsValues,
        languagesValues,
        certificationsValues,
        personalValues,
      } = profileToFormValues(profileResponse);

      setExperiencesValues(experiencesValues);
      setFormationsValues(formationsValues);
      setLanguagesValues(languagesValues);
      setCertificationsValues(certificationsValues);
      setPersonalValues(personalValues);
    }
  }, [profileResponse]);

  const getRecommendation = (
    category: RecommendationCategory,
    profileResponse: ParsedProfileResponse | null
  ) => {
    if (!profileResponse?.profile) return undefined;

    let count = 0;

    switch (category) {
      case 'summary':
        if (
          profileResponse.profile.summary_recommendation &&
          !resolvedRecommendations.includes('summary_recommendation')
        )
          count++;
        break;
      case 'experiences':
        count =
          profileResponse.profile.experiences?.filter(
            (exp, index) =>
              (exp.description_recommendation &&
                !resolvedRecommendations.includes(`exp.${index}.description_recommendation`)) ||
              (exp.date_recommendation &&
                !resolvedRecommendations.includes(`exp.${index}.date_recommendation`))
          ).length || 0;
        break;
      case 'education':
        count =
          profileResponse.profile.educations?.filter(
            (edu, index) =>
              (edu.description_recommendation &&
                !resolvedRecommendations.includes(`edu.${index}.description_recommendation`)) ||
              (edu.date_recommendation &&
                !resolvedRecommendations.includes(`edu.${index}.date_recommendation`))
          ).length || 0;
        break;
    }

    return count > 0 ? t('recommandation', { count }) : undefined;
  };

  // useMemo to check if the personal info is complete
  // email, completeName, location, headline, summary
  const getPersonalInfoError = useMemo(() => {
    if (
      !personalValues.email ||
      !personalValues.completeName ||
      !personalValues.location ||
      !personalValues.headline ||
      !personalValues.summary
    ) {
      return t('edit-profile.error_required.personal_info');
    }
    return undefined;
  }, [personalValues, t]);

  // at leat one experience or education
  const getExperienceOrEducationError = useMemo(() => {
    if (experiencesValues.experiences.length === 0 && formationsValues.formations.length === 0) {
      return t('edit-profile.error_required.experience_education');
    }
    return undefined;
  }, [experiencesValues, formationsValues, t]);

  // at least one language
  const getLanguageError = useMemo(() => {
    if (languagesValues.languages.length === 0) {
      return t('edit-profile.error_required.languages');
    }
    return undefined;
  }, [languagesValues, t]);

  // for each certifications, check url, title, date, deliveredBy
  const getCertificationError = useMemo(() => {
    if (
      certificationsValues.certifications.some(
        cert => !cert.url || !cert.title || !cert.date || !cert.deliveredBy
      )
    ) {
      return t('edit-profile.error_required.certifications');
    }
    return undefined;
  }, [certificationsValues, t]);

  // check experiences companyName, title, description, startDate, location
  const getExperienceError = useMemo(() => {
    if (
      experiencesValues.experiences.some(
        exp => !exp.companyName || !exp.title || !exp.description || !exp.startDate || !exp.location
      )
    ) {
      return t('edit-profile.error_required.experience');
    }
    return undefined;
  }, [experiencesValues, t]);

  // check formations title, school, description, startDate, location
  const getFormationError = useMemo(() => {
    if (
      formationsValues.formations.some(f => !f.title || !f.school || !f.description || !f.startDate)
    ) {
      return t('edit-profile.error_required.education');
    }
    return undefined;
  }, [formationsValues, t]);

  const everyThingIsComplete = useMemo(() => {
    return (
      !getPersonalInfoError &&
      !getExperienceOrEducationError &&
      !getLanguageError &&
      !getCertificationError &&
      !getExperienceError &&
      !getFormationError
    );
  }, [
    getPersonalInfoError,
    getExperienceOrEducationError,
    getLanguageError,
    getCertificationError,
    getExperienceError,
    getFormationError,
  ]);

  const missingInformation = t('edit-profile.error_required.title');

  const handleSubmit = (profileResponse: ParsedProfileResponse | null) => {
    setSubmitted(true);
    if (everyThingIsComplete) {
      const updatedProfile = formValuesToProfile(
        experiencesValues,
        formationsValues,
        languagesValues,
        certificationsValues,
        personalValues
      );

      console.log('updatedProfile', updatedProfile);

      if (updatedProfile) {
        updateProfile(updatedProfile);
      }
    }
    if (!everyThingIsComplete) {
      // show Modal.warning
      Modal.error({
        title: missingInformation,
        content: t('edit-profile.error_required.description'),
      });
    } else if (
      getRecommendation('summary', profileResponse) ||
      getRecommendation('experiences', profileResponse) ||
      getRecommendation('education', profileResponse)
    ) {
      // show Modal.warning
      Modal.confirm({
        width: 500,
        title: t('edit-profile.recommendations.remember.title'),
        content: t('edit-profile.recommendations.remember.description'),
        okText: t('edit-profile.recommendations.remember.mark_as_resolved'),
        cancelText: t('edit-profile.recommendations.remember.ignore'),
        onCancel: () => {
          navigate({
            to: '/get-offer-details',
          });
        },
        onOk: () => {},
      });
    } else if (
      (method === 'fill-from-zero' || !profileResponse?.profile) &&
      !evaluateOnce.current
    ) {
      setIsEvaluateCVModalOpen(true);
      evaluateOnce.current = true;
    } else {
      Modal.success({
        title: t('edit-profile.success.title'),
        content: t('edit-profile.success.description'),
        okText: t('global.continue'),
        onOk: () => {
          navigate({ to: '/get-offer-details' });
        },
      });
    }
  };

  const handleEvaluateCV = async () => {
    const res = await evaluateCV();
    setIsEvaluateCVModalOpen(false);
    handleSubmit(res);
  };

  return (
    <>
      <Modal
        open={isEvaluateCVModalOpen}
        title={t('edit-profile.check_again.title')}
        footer={[
          <Button
            key="cancel"
            type="default"
            disabled={isLoading}
            onClick={() => setIsEvaluateCVModalOpen(false)}
          >
            {t('global.cancel')}
          </Button>,
          <Button key="evaluate" type="primary" onClick={handleEvaluateCV} loading={isLoading}>
            {t('edit-profile.check_again.evaluate')}
          </Button>,
        ]}
      >
        <p>{isLoading ? currentMessage : t('edit-profile.check_again.description')}</p>
      </Modal>
      <PersonalInfosFormDialog
        open={personalFormOpen}
        onClose={() => setPersonalFormOpen(false)}
        onFinished={setPersonalValues}
        initialValues={personalValues}
      />

      <ExperiencesFormDialog
        open={experiencesDialogOpen}
        onClose={() => setExperiencesDialogOpen(false)}
        onFinished={setExperiencesValues}
        initialValues={experiencesValues}
      />

      <FormationsFormDialog
        open={formationDialogOpen}
        onClose={() => setFormationDialogOpen(false)}
        onFinished={setFormationsValues}
        initialValues={formationsValues}
      />

      <CertificationsFormDialog
        open={certificationsDialogOpen}
        onClose={() => setCertificationsDialogOpen(false)}
        onFinished={setCertificationsValues}
        initialValues={certificationsValues}
      />

      <LanguagesFormDialog
        open={languagesDialogOpen}
        onClose={() => setLanguagesDialogOpen(false)}
        onFinished={setLanguagesValues}
        initialValues={languagesValues}
      />

      <div className="lg:h-screen overflow-auto w-full flex flex-col gap-8 items-center justify-center overflow-y-auto p-4">
        <header className="text-center pt-12">
          <h1 className="h1 mb-2">{t('edit-profile.title')}</h1>
          <p className="body-1 text-gray">
            <span className="text-primary">{t('edit-profile.subtitle-highlight')}</span>
            {t('edit-profile.subtitle')}
          </p>
        </header>

        <main className="flex flex-col gap-4 items-stretch w-full max-w-[800px]">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Card
              title={t('edit-profile.personal-info.title')}
              description={personalValues.completeName || t('global.no_information')}
              onClick={() => setPersonalFormOpen(true)}
              className="bg-white"
              icon={<EditOutlined />}
              maxSubtitleLines={2}
              error={submitted ? getPersonalInfoError : undefined}
              warning={getRecommendation('summary', profileResponse)}
            />
            <Card
              title={t('edit-profile.experience.title')}
              description={
                experiencesValues.experiences.length > 0
                  ? experiencesValues.experiences.map(exp => exp.title).join(', ')
                  : t('global.no_information')
              }
              onClick={() => setExperiencesDialogOpen(true)}
              className="bg-white"
              icon={<EditOutlined />}
              maxSubtitleLines={2}
              error={submitted ? getExperienceOrEducationError || getExperienceError : undefined}
              warning={getRecommendation('experiences', profileResponse)}
            />
            <Card
              title={t('edit-profile.education.title')}
              description={
                formationsValues.formations.length > 0
                  ? formationsValues.formations.map(f => `${f.title} - ${f.school}`).join(', ')
                  : t('global.no_information')
              }
              onClick={() => setFormationDialogOpen(true)}
              className="bg-white"
              icon={<EditOutlined />}
              maxSubtitleLines={2}
              error={submitted ? getExperienceOrEducationError || getFormationError : undefined}
              warning={getRecommendation('education', profileResponse)}
            />
            <Card
              title={t('edit-profile.certifications.title')}
              description={
                certificationsValues.certifications.length > 0
                  ? certificationsValues.certifications.map(c => c.title).join(', ')
                  : t('global.no_information')
              }
              onClick={() => setCertificationsDialogOpen(true)}
              className="bg-white"
              icon={<EditOutlined />}
              maxSubtitleLines={2}
              error={submitted ? getCertificationError : undefined}
            />
          </div>
          <Card
            title={t('edit-profile.languages.title')}
            description={
              languagesValues.languages.length > 0
                ? languagesValues.languages.map(l => l.language).join(', ')
                : t('global.no_information')
            }
            onClick={() => setLanguagesDialogOpen(true)}
            className="bg-white"
            icon={<EditOutlined />}
            maxSubtitleLines={2}
            error={submitted ? getLanguageError : undefined}
          />
          <div className="w-full max-w-[450px] mx-auto mt-6 flex flex-col gap-4">
            <Button
              onClick={() => handleSubmit(profileResponse)}
              type="primary"
              icon={<ArrowRightOutlined />}
              iconPosition="end"
              size="large"
              className="w-full"
            >
              {t('global.continue')}
            </Button>
            <Link to="/import-profile">
              <Button
                type="dashed"
                icon={<ArrowLeftOutlined />}
                iconPosition="start"
                size="large"
                className="w-full"
              >
                {t('global.come-back')}
              </Button>
            </Link>
          </div>
        </main>
      </div>
    </>
  );
}
